<template>
    <div class="root-container">
        <div class="left-pane">
            <MenuPane />
        </div>
        <div class="center-pane" ref="centerPane" @scroll="handleScroll">
            <FloatingActionButton class="fab" />
            <div v-if="!selectedPost" class="main-feed">
                <div class="center-top-container">
                    <PostCreator />
                </div>
                <div class="post-container" v-for="post in posts" :key="post.id" @click="selectPost(post)">
                    <ContentItem :item="post" :timeElapsed="calculateTimeElapsed(post.created_at)" type="post" />
                </div>
                <Loading :isLoadingMore="isLoadingMorePosts" :hasNoMore="!hasMorePosts && posts.length > 0"
                    type="posts" />
            </div>

            <div v-else class="post-detail-view">
                <div class="back-button" v-if="selectedPosts.length > 0" @click="selectedPosts.pop()">
                    ← Back
                </div>
                <div class="main-post">
                    <ContentItem :item="selectedPost" :timeElapsed="calculateTimeElapsed(selectedPost.created_at)"
                        type="post" />
                </div>

                <div class="replies-section" :class="{ 'has-border': shouldShowBorder }">
                    <div v-if="isLoadingReplies" class="loading-replies">
                        Loading replies...
                    </div>
                    <div v-else-if="replies.length === 0" class="no-replies">
                        No replies yet
                    </div>
                    <div v-else class="replies-list">
                        <div class="reply" v-for="reply in replies" :key="reply.id" @click.stop="selectPost(reply)">
                            <ContentItem :item="reply" :timeElapsed="calculateTimeElapsed(reply.created_at)"
                                type="reply" />
                        </div>
                        <Loading :isLoadingMore="isLoadingMoreReplies" :hasNoMore="hasMoreReplies && replies.length > 0"
                            type="replies" />
                        <div v-if="hasMoreReplies && replies.length > 0" class="load-more-button"
                            @click="loadMoreReplies">
                            Load more replies
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="right-pane"></div>
    </div>
</template>

<script>

const POSTS_PER_PAGE = 10;
const REPLIES_PER_PAGE = 10;

import { calculateTimeElapsed } from '@/util.js';
import MenuPane from '@/components/MenuPane.vue';
import PostService from '@/service/post.js';
import PostCreator from '@/components/home/PostCreator.vue';
import ContentItem from '@/components/home/ContentItem.vue';
import Loading from '@/components/home/Loading.vue';
import FloatingActionButton from '@/components/home/FloatingActionButton.vue';
export default {
    name: 'Home',
    components: {
        MenuPane,
        PostCreator,
        ContentItem,
        Loading,
        FloatingActionButton
    },
    async mounted() {
        await this.getRecommendedPosts(0);
    },
    data() {
        return {
            postService: new PostService(),
            posts: [],
            currentOffset: 0,
            hasMorePosts: true,
            isLoadingMorePosts: false,
            selectedPosts: [],
            replies: [],
            isLoadingReplies: false,
            repliesOffset: 0,
            hasMoreReplies: true,
            isLoadingMoreReplies: false
        }
    },
    computed: {
        selectedPost() {
            if (this.selectedPosts.length > 0) {
                return this.selectedPosts[this.selectedPosts.length - 1];
            }
            return null;
        },
        shouldShowBorder() {
            return !this.isLoadingReplies && this.replies.length > 0;
        }
    },
    methods: {
        calculateTimeElapsed,
        async getRecommendedPosts(offset) {
            this.isLoadingMorePosts = true;
            let res = await this.postService.getRecommendedPosts(offset, POSTS_PER_PAGE);
            if (res.error === null) {
                let newPosts = res.data;
                if (newPosts.length < POSTS_PER_PAGE) {
                    this.hasMorePosts = false;
                }
                this.posts = [...this.posts, ...newPosts];
                this.currentOffset += newPosts.length;
            } else if (res.error?.status === 401){
                this.$router.push('/');
            } else {
                console.error('Error fetching posts:', res.error);
            }
            this.isLoadingMorePosts = false;
        },
        handleScroll(event) {
            if (this.selectedPost) return;

            const element = event.target;
            const scrollBottom = element.scrollHeight - element.scrollTop - element.clientHeight;

            if (scrollBottom < 100 && !this.isLoadingMorePosts && this.hasMorePosts) {
                this.loadMorePosts();
            }
        },
        async loadMorePosts() {
            if (this.isLoadingMorePosts || !this.hasMorePosts) return;
            await this.getRecommendedPosts(this.currentOffset);
        },
        selectPost(post) {
            if (this.selectedPost?.id === post.id) {
                this.selectedPosts.pop();
            } else {
                this.selectedPosts.push(post);
                this.replies = [];
                this.repliesOffset = 0;
                this.hasMoreReplies = true;
            }
        },
        async fetchReplies(post, offset, flag_key) {
            this[flag_key] = true;
            let res = await this.postService.fetchReplies(
                post.id, offset, REPLIES_PER_PAGE,
            );
            if (res.error === null) {
                const newReplies = res.data;
                if (newReplies.length < REPLIES_PER_PAGE) {
                    this.hasMoreReplies = false;
                }
                this.replies = [...this.replies, ...newReplies];
                this.repliesOffset += newReplies.length;
            } else if (res.error?.status === 401) {
                this.$router.push('/');
            } else {
                console.error('Error fetching replies:', res.error);
            }
            this[flag_key] = false;
        },
        async loadMoreReplies() {
            if (this.isLoadingMoreReplies || !this.hasMoreReplies) return;
            let post = this.selectedPost;
            if (post === null) return;
            await this.fetchReplies(post, this.repliesOffset, "isLoadingMoreReplies");
        },
    },
    watch: {
        async selectedPost(newVal) {
            if (newVal === null) {
                this.replies = [];
                return;
            }
            if (this.isLoadingReplies) return;
            this.replies = [];
            this.repliesOffset = 0;
            this.hasMoreReplies = true;
            await this.fetchReplies(newVal, 0, "isLoadingReplies");
        }
    }
}
</script>

<style lang="scss" scoped>
$color-bg-primary: black;
$color-bg-secondary: #0a0a0a;
$color-bg-hover: #1a1a1a;

$color-text-primary: #ddd;
$color-text-secondary: #888;
$color-text-tertiary: #666;

$color-border: #333;
$color-accent: #1da1f2;

$transition-normal: background-color 0.3s;
$transition-fast: background-color 0.2s;


.root-container {
    display: flex;
    width: 100vw;
    min-height: 100vh;
    background-color: $color-bg-primary;

    .left-pane {
        display: flex;
        width: 20%;
        background-color: $color-bg-primary;
        border-right: 1px solid $color-border;
        flex-direction: column;
    }

    .center-pane {
        display: flex;
        width: 50%;
        background-color: $color-bg-primary;
        flex-direction: column;
        border-right: 1px solid $color-border;
        overflow-y: auto;
        height: 100vh;

        .fab {
            display: none;
        }

        .main-feed {
            width: 100%;

            .center-top-container {
                padding-left: 20px;
                padding-right: 20px;
                height: 30vh;
                background-color: $color-bg-primary;
                border-bottom: 1px solid $color-border;
                display: flex;
                justify-content: center;
                align-items: center;
            }

            .post-container {
                color: $color-text-primary;
                font-size: 14px;
                padding-left: 20px;
                padding-right: 20px;
                padding-top: 20px;
                padding-bottom: 20px;
                border-bottom: 1px solid $color-border;
                font-weight: 400;
                font-family: 'Helvetica', 'Arial', sans-serif;
                line-height: 1.5;
                cursor: pointer;
                transition: $transition-normal;
            }

            .post-container:hover {
                background-color: $color-bg-hover;
            }
        }

        .post-detail-view {
            padding: 20px;
            height: 100%;
            overflow-y: auto;

            .back-button {
                color: $color-text-secondary;
                padding: 10px 0;
                margin-bottom: 20px;
                cursor: pointer;
                transition: color 0.3s;
            }

            .back-button:hover {
                color: $color-text-primary;
            }

            .main-post {
                padding: 20px;
                background-color: $color-bg-secondary;
                border-radius: 8px;
                margin-bottom: 30px;
                color: $color-text-primary;
                transition: $transition-normal;
            }

            .main-post:hover {
                background-color: $color-bg-hover;
            }

            .replies-section {
                margin-left: 30px;
                padding-left: 20px;
                color: $color-text-primary;

                .loading-replies {
                    padding: 15px;
                    color: $color-text-secondary;
                    text-align: center;
                }

                .no-replies {
                    padding: 15px;
                    color: $color-text-tertiary;
                    text-align: center;
                    font-style: italic;
                }

                .replies-list {
                    margin-top: 10px;

                    .reply {
                        margin: 15px 0;
                        padding: 15px;
                        background-color: $color-bg-secondary;
                        border-radius: 6px;
                        cursor: pointer;
                        transition: $transition-normal;
                    }

                    .reply:hover {
                        background-color: $color-bg-hover;
                    }

                    .load-more-button {
                        text-align: center;
                        padding: 10px;
                        margin: 10px 0;
                        color: $color-accent;
                        background-color: $color-bg-secondary;
                        border-radius: 6px;
                        cursor: pointer;
                        transition: $transition-fast;
                    }

                    .load-more-button:hover {
                        background-color: $color-bg-hover;
                    }
                }
            }

            .replies-section.has-border {
                border-left: 2px solid $color-border;
            }
        }
    }

    .right-pane {
        display: flex;
        width: 30%;
        background-color: $color-bg-primary;
    }
}

@media (max-width: 768px) {
    .left-pane {
        display: none !important;
    }

    .right-pane {
        display: none !important;
    }

    .center-pane {
        width: 100% !important;
    }

    .fab {
        display: flex !important;
    }
}
</style>
